// AgGridAdapter — the only place in the frontend that knows about AgGrid's wire format.
// All service calls should go through toQueryRequest() rather than sending AgGrid params directly.

import type { IServerSideGetRowsRequest } from 'ag-grid-community'
import type {
  QueryFilter,
  QueryRequest,
  QuerySort,
  TextOperator,
  CompareOperator,
  JoinOperator,
} from './QueryRequest'

type AgGridFilter = Record<string, AgGridColumnFilter>

type AgGridColumnFilter =
  | AgGridScalarFilter
  | AgGridSetFilter
  | AgGridJoinFilter

type AgGridScalarFilter = {
  filterType: 'text' | 'number' | 'date' | 'boolean'
  type: string
  filter?: unknown
  dateFrom?: string
}

type AgGridSetFilter = {
  filterType: 'set'
  values: string[]
}

type AgGridJoinFilter = {
  filterType: 'join'
  type: 'and' | 'or'
  conditions: AgGridColumnFilter[]
}

function mapTextOperator(type: string): TextOperator {
  const map: Record<string, TextOperator> = {
    equals: 'Equals',
    notEqual: 'NotEqual',
    contains: 'Contains',
    notContains: 'NotContains',
    startsWith: 'StartsWith',
    endsWith: 'EndsWith',
    blank: 'Blank',
    notBlank: 'NotBlank',
  }
  return map[type] ?? 'Equals'
}

function mapCompareOperator(type: string): CompareOperator {
  const map: Record<string, CompareOperator> = {
    equals: 'Equals',
    notEqual: 'NotEqual',
    lessThan: 'LessThan',
    lessThanOrEqual: 'LessThanOrEqualTo',
    greaterThan: 'GreaterThan',
    greaterThanOrEqual: 'GreaterThanOrEqualTo',
  }
  return map[type] ?? 'Equals'
}

function mapJoinOperator(type: string): JoinOperator {
  return type === 'or' ? 'Or' : 'And'
}

function mapColumnFilter(agFilter: AgGridColumnFilter): QueryFilter | null {
  if (agFilter.filterType === 'set') {
    return { filterType: 'set', values: (agFilter as AgGridSetFilter).values ?? [] }
  }

  if (agFilter.filterType === 'join') {
    const join = agFilter as AgGridJoinFilter
    const conditions = join.conditions.flatMap((c) => {
      const mapped = mapColumnFilter(c)
      return mapped ? [mapped] : []
    })
    return { filterType: 'composite', operator: mapJoinOperator(join.type), conditions }
  }

  const scalar = agFilter as AgGridScalarFilter

  if (scalar.filterType === 'text') {
    return { filterType: 'string', value: String(scalar.filter ?? ''), operator: mapTextOperator(scalar.type) }
  }

  if (scalar.filterType === 'number') {
    return { filterType: 'number', value: Number(scalar.filter ?? 0), operator: mapCompareOperator(scalar.type) }
  }

  if (scalar.filterType === 'date') {
    return { filterType: 'date', value: String(scalar.dateFrom ?? ''), operator: mapCompareOperator(scalar.type) }
  }

  if (scalar.filterType === 'boolean') {
    return { filterType: 'boolean', value: Boolean(scalar.filter) }
  }

  return null
}

export function toQueryRequest<TColumn extends string>(
  params: IServerSideGetRowsRequest & { search?: string },
  fullTextColumn?: TColumn,
): QueryRequest<TColumn> {
  const filters: Partial<Record<TColumn, QueryFilter>> = {}

  for (const [col, agFilter] of Object.entries(params.filterModel ?? {} as AgGridFilter)) {
    const mapped = mapColumnFilter(agFilter as AgGridColumnFilter)
    if (mapped) {
      filters[col as TColumn] = mapped
    }
  }

  if (params.search && fullTextColumn) {
    filters[fullTextColumn] = { filterType: 'string', value: params.search, operator: 'Equals' }
  }

  const sorts: QuerySort<TColumn>[] = (params.sortModel ?? []).map((s) => ({
    column: s.colId as TColumn,
    direction: s.sort === 'desc' ? 'Desc' : 'Asc',
  }))

  const offset = params.startRow ?? 0
  const limit = (params.endRow ?? 100) - offset

  return { filters, sorts, offset, limit }
}
